import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

// Custom SplitText implementation
class SplitText {
    constructor(element, options = {}) {
        this.element = element;
        this.options = options;
        this.chars = [];
        this.originalHTML = element.innerHTML;

        this.split();
    }

    split() {
        const { type = 'chars', charsClass = '' } = this.options;
        const text = this.element.textContent;

        if (type.includes('chars')) {
            this.element.innerHTML = '';
            const chars = text.split('');

            chars.forEach((char) => {
                const charSpan = document.createElement('span');
                charSpan.className = charsClass;
                charSpan.textContent = char;
                charSpan.style.display = 'inline-block';
                this.chars.push(charSpan);
                this.element.appendChild(charSpan);
            });
        }
    }

    static create(element, options) {
        return new SplitText(element, options);
    }

    revert() {
        if (this.element) {
            this.element.innerHTML = this.originalHTML;
        }
    }
}

// Custom scramble text animation function
const scrambleText = (element, targetText, chars, duration, speed) => {
    const charArray = chars.split('');
    let currentText = element.textContent;
    const startTime = Date.now();

    const animate = () => {
        const elapsed = (Date.now() - startTime) / 1000;
        const progress = Math.min(elapsed / duration, 1);

        if (progress < 1) {
            // Scramble characters
            let scrambled = '';
            for (let i = 0; i < targetText.length; i++) {
                if (Math.random() > progress) {
                    scrambled += charArray[Math.floor(Math.random() * charArray.length)];
                } else {
                    scrambled += targetText[i];
                }
            }
            element.textContent = scrambled;
            requestAnimationFrame(animate);
        } else {
            element.textContent = targetText;
        }
    };

    animate();
};

const ScrambledText = ({
    radius = 100,
    duration = 1.2,
    speed = 0.5,
    scrambleChars = '.:',
    className = '',
    style = {},
    children
}) => {
    const rootRef = useRef(null);

    useEffect(() => {
        if (!rootRef.current) return;

        const split = SplitText.create(rootRef.current.querySelector('p'), {
            type: 'chars',
            charsClass: 'inline-block will-change-transform'
        });

        split.chars.forEach(el => {
            const c = el;
            gsap.set(c, { attr: { 'data-content': c.innerHTML } });
        });

        const handleMove = e => {
            split.chars.forEach(el => {
                const c = el;
                const { left, top, width, height } = c.getBoundingClientRect();
                const dx = e.clientX - (left + width / 2);
                const dy = e.clientY - (top + height / 2);
                const dist = Math.hypot(dx, dy);

                if (dist < radius) {
                    const targetText = c.dataset.content || '';
                    const adjustedDuration = duration * (1 - dist / radius);

                    // Use custom scramble animation
                    scrambleText(c, targetText, scrambleChars, adjustedDuration, speed);
                }
            });
        };

        const el = rootRef.current;
        el.addEventListener('pointermove', handleMove);

        return () => {
            el.removeEventListener('pointermove', handleMove);
            split.revert();
        };
    }, [radius, duration, speed, scrambleChars]);

    return (
        <div
            ref={rootRef}
            className={`m-[7vw] max-w-[800px] font-mono text-[clamp(14px,4vw,32px)] text-white ${className}`}
            style={style}
        >
            <p>{children}</p>
        </div>
    );
};

export default ScrambledText;
